import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Login.css'; // Create this file for animations
import Swal from 'sweetalert2';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    phone: '+263',
    password: ''
  });
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' | 'phone'
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const base_url = 'https://api.homehive.co.zw'

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Ensure it always starts with +263
    if (!value.startsWith('+263')) {
      setFormData({
        ...formData,
        phone: '+263'
      });
    } else {
      setFormData({
        ...formData,
        phone: value
      });
    }
  };

  const handlePhoneFocus = (e) => {
    // Position cursor after +263
    const input = e.target;
    setTimeout(() => {
      input.setSelectionRange(4, 4);
    }, 0);
  };

  const handleForgotPassword = async () => {
    const { value: email } = await Swal.fire({
      title: 'Password Reset',
      input: 'email',
      inputLabel: 'Enter your email address',
      inputPlaceholder: 'email@example.com',
      showCancelButton: true,
      confirmButtonText: 'Send Reset Link',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#1a237e',
      showLoaderOnConfirm: true,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter your email address';
        }
        if (!/\S+@\S+\.\S+/.test(value)) {
          return 'Please enter a valid email address';
        }
      },
      preConfirm: async (email) => {
        try {
          setIsResetLoading(true);
          const response = await axios.post(`${base_url}/api/password/reset/`, { email });
          return response.data;
        } catch (error) {
          console.log(error)
          Swal.showValidationMessage(
            error.response?.data?.message || 'Failed to send reset link'
          );
        } finally {
          setIsResetLoading(false);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    });

    if (email) {
      Swal.fire({
        icon: 'success',
        title: 'Reset Link Sent!',
        text: 'Please check your email for instructions to reset your password.',
        confirmButtonColor: '#1a237e'
      });
    }
  };

  const normalizePhoneToE164 = (raw) => {
    if (!raw) return '';
    let phone = ('' + raw).replace(/\s|-/g, '');
    if (phone.startsWith('+263')) return phone;
    if (phone.startsWith('263')) return `+${phone}`;
    if (phone.startsWith('0')) return `+263${phone.slice(1)}`;
    if (/^\d+$/.test(phone)) return `+263${phone}`;
    return phone;
  };

  const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);
  const isValidZimPhone = (value) => /^\+263\d{9,10}$/.test(value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      let payload = { password: formData.password };

      if (loginMethod === 'email') {
        if (!formData.email || !isValidEmail(formData.email)) {
          throw { response: { data: { detail: 'Please enter a valid email address.' } } };
        }
        payload.email = formData.email.trim();
      } else {
        const normalized = normalizePhoneToE164(formData.phone);
        if (!normalized.startsWith('+263')) {
          throw { response: { data: { detail: 'Phone must include country code +263.' } } };
        }
        if (!isValidZimPhone(normalized)) {
          throw { response: { data: { detail: 'Please enter a valid +263 phone number.' } } };
        }
        payload.phone = normalized;
      }

      const response = await axios.post(base_url + '/api/login/', payload);
      
      // Store the tokens
      localStorage.setItem('token', response.data.access);
      console.log("Token: ", response)




      // Get user data
      const userResponse = await axios.get(base_url + '/api/user', {
        headers: {
          'Authorization': `Bearer ${response.data.access}`
        }
      });
      console.log("User data: ", userResponse.data)

      // Store user data if needed
      localStorage.setItem('user', JSON.stringify(userResponse.data));
      console.log(userResponse)
      // Redirect based on user role
      if (userResponse.data.user.is_staff) {
        navigate('/admin/dashboard');
      } else if (userResponse.data.user.is_landlord) {
        navigate('/landlord/dashboard');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(
        err.response?.data?.non_field_errors[0] || 
        'Invalid credentials. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
      padding: '20px',
      margin: 0,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    },
    formCard: {
      background: 'white',
      padding: '40px',
      borderRadius: '10px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '400px',
      minHeight: '420px',
      animation: 'slideIn 0.5s ease-out'
    },
    title: {
      textAlign: 'center',
      color: '#1a237e',
      marginBottom: '30px',
      fontSize: '24px',
      fontWeight: 'bold'
    },
    formGroup: {
      marginBottom: '20px'
    },
    methodToggle: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px'
    },
    methodButton: {
      flex: 1,
      padding: '10px 12px',
      borderRadius: '6px',
      border: '1px solid #ddd',
      background: '#f7f7f7',
      cursor: 'pointer',
      fontWeight: '500'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      color: '#333',
      fontSize: '14px',
      fontWeight: '500'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      transition: 'border-color 0.3s ease'
    },
    passwordContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    },
    passwordInput: {
      width: '100%',
      padding: '12px 45px 12px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      transition: 'border-color 0.3s ease'
    },
    passwordToggle: {
      position: 'absolute',
      right: '12px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#666',
      fontSize: '16px',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'color 0.3s ease'
    },
    button: {
      width: '100%',
      padding: '14px',
      background: '#1a237e',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    error: {
      color: '#dc3545',
      marginBottom: '20px',
      padding: '10px',
      background: '#fff3f3',
      borderRadius: '4px',
      fontSize: '14px'
    },
    signupText: {
      textAlign: 'center',
      marginTop: '20px',
      color: '#666',
      fontSize: '14px'
    },
    signupLink: {
      color: '#1a237e',
      textDecoration: 'none',
      fontWeight: '500',
      marginLeft: '5px',
      transition: 'color 0.3s ease'
    },
    backButton: {
      position: 'absolute',
      top: '20px',
      left: '20px',
      background: 'rgba(255, 255, 255, 0.2)',
      color: '#fff',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '30px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      backdropFilter: 'blur(5px)',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    },
    backButtonIcon: {
      fontSize: '18px',
    },
    forgotPassword: {
      textAlign: 'right',
      marginTop: '-10px',
      marginBottom: '20px'
    },
    forgotPasswordLink: {
      color: '#1a237e',
      textDecoration: 'none',
      fontSize: '14px',
      transition: 'color 0.3s ease',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px'
    },
  };

  return (
    <div style={styles.container} className="login-container">
      <Link 
        to="/" 
        style={styles.backButton}
        className="back-button"
      >
        <i className="fas fa-arrow-left" style={styles.backButtonIcon}></i>
        Back to Home
      </Link>

      <div className="background-squares">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="square"></div>
        ))}
      </div>
      
      <div style={styles.formCard} className="form-card">
        <h2 style={styles.title} className="title-animate">Login to HomeHive</h2>
        
        {error && (
          <div style={styles.error} className="error-animate">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="form-animate">
          <div style={styles.methodToggle}>
            <button
              type="button"
              style={{
                ...styles.methodButton,
                background: loginMethod === 'email' ? '#e8eaf6' : '#f7f7f7',
                borderColor: loginMethod === 'email' ? '#1a237e' : '#ddd',
                color: loginMethod === 'email' ? '#1a237e' : '#333'
              }}
              onClick={() => {
                setLoginMethod('email');
                setFormData({...formData, phone: '+263'});
              }}
            >
              Email
            </button>
            <button
              type="button"
              style={{
                ...styles.methodButton,
                background: loginMethod === 'phone' ? '#e8eaf6' : '#f7f7f7',
                borderColor: loginMethod === 'phone' ? '#1a237e' : '#ddd',
                color: loginMethod === 'phone' ? '#1a237e' : '#333'
              }}
              onClick={() => {
                setLoginMethod('phone');
                setFormData({...formData, phone: '+263'});
              }}
            >
              Phone
            </button>
          </div>
          <div style={styles.formGroup} className="form-group-animate">
            {loginMethod === 'email' ? (
              <>
                <label style={styles.label} htmlFor="email">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  style={styles.input}
                  className="input-animate"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                />
              </>
            ) : (
              <>
                <label style={styles.label} htmlFor="phone">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  style={styles.input}
                  className="input-animate"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onFocus={handlePhoneFocus}
                  required
                  placeholder="+263771234567"
                />
              </>
            )}
          </div>

          <div style={styles.formGroup} className="form-group-animate">
            <label style={styles.label} htmlFor="password">
              Password
            </label>
            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                style={styles.passwordInput}
                className="input-animate"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
              />
              <button
                type="button"
                style={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? "Hide password" : "Show password"}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div style={styles.forgotPassword}>
            <button 
              onClick={handleForgotPassword} 
              type="button"
              style={{
                ...styles.forgotPasswordLink,
                border: 'none',
                background: 'none',
                padding: 0
              }}
            >
              <i className="fas fa-key" style={{ fontSize: '12px' }}></i>
              Forgot Password?
            </button>
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '16px' }}></i>
                <span>Logging in...</span>
              </>
            ) : (
              'Login'
            )}
          </button>

          <div style={styles.signupText}>
            Don't have an account?
            <Link to="/signup" style={styles.signupLink}>
              Sign up here
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 